import { yupResolver } from "@hookform/resolvers/yup";
import { useQuery } from "@apollo/client";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  Badge,
  Card,
  FlatActionList,
  Form,
  Page,
  Button,
  Stack,
  Text,
} from "@towersystems/roam-common-native";
import { useEffect } from "react";
import { ActivityIndicator, Pressable } from "react-native";
import { FormProvider, useForm } from "react-hook-form";
import { Colors, Spacings, View } from "react-native-ui-lib";
import { CustomerRootParamList } from "../../../../types";
import CustomerModel from "../../../../utilities/data/model/Customer";
import { normalizeSelectedValues } from "../../../../utilities/transforms";
import { CustomerDetailsForm } from "../../components/CustomerDetailsForm";
import {
  CustomerFormValues,
  customerInputSchema,
  defaultCustomerInputValues,
  useMutationUpdateCustomer,
  QUERY_CUSTOMER,
} from "../../utils/customer";
import { useSaleControllerProvider } from "../../utils/sale-controller";
import { useActionBackOrClose } from "../../utils/use-action-back-or-close";
import {
  Customer,
  Sale,
} from "@towersystems/roam-common/lib/generated-types";
import { QUERY_SALE_LIST } from "../../../sales/utils/sales/constants";
import { moneyToFormattedString } from "../../../../utilities/money";
import {
  bankersRound,
  formatDateToCalendar,
  fullDateTimeFormat,
} from "@towersystems/roam-common/lib/shared-utils";

export type EditCustomerScreenProps = NativeStackScreenProps<
  CustomerRootParamList,
  "EditCustomerScreen"
>;

// this screen should probaly be written in the context
// that its for the active sale customer
export function EditCustomerScreen({
  navigation,
  route,
}: EditCustomerScreenProps) {
  const { sale } = useSaleControllerProvider();
  const database = useDatabase();
  const customerId = route.params.customerId;
  useActionBackOrClose(navigation);

  const { data } = useQuery<{ customer: Customer }>(QUERY_CUSTOMER, {
    variables: { id: customerId },
  });

  const { data: salesData, loading: salesLoading } = useQuery<{
    sales: { items: Sale[] };
  }>(QUERY_SALE_LIST, {
    variables: {
      options: {
        filter: { customerId: { in: [customerId] } },
        sort: { saleDate: "DESC" },
        limit: 10,
        page: 1,
      },
    },
  });

  const methods = useForm<CustomerFormValues>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: normalizeSelectedValues(defaultCustomerInputValues, {}),
    resolver: yupResolver(customerInputSchema),
  });

  useEffect(() => {
    database
      .get<CustomerModel>("customers")
      .find(customerId)
      .then((cus: CustomerModel) => {
        methods.reset({
          ...normalizeSelectedValues(defaultCustomerInputValues, {
            id: cus.id,
            firstName: cus.firstName,
            lastName: cus.lastName,
            emailAddress: cus.emailAddress,
            mobileNumber: cus.mobileNumber,
            phoneNumber: cus.phoneNumber,
          }),
        });
      })
      .catch((err) => console.log(err));
  }, [customerId]);

  const {
    formState: { isValid, isDirty },
  } = methods;

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <Button
          label="Reset"
          isAction
          disabled={!isDirty}
          onPress={() => methods.reset()}
          size="compact"
        ></Button>
      ),
    });
  }, [isDirty]);

  const { updateCustomer } = useMutationUpdateCustomer();

  async function onValidSubmit(formValues: any) {
    updateCustomer(formValues).then(async (customer) => {
      navigation.goBack();

      const localCustomer = await database
        .get<CustomerModel>("customers")
        .find(customer.id);

      await database.write(async () => {
        localCustomer.update((customerLocal) => {
          customerLocal.firstName = customer.firstName;
          customerLocal.lastName = customer.lastName;
          customerLocal.emailAddress = customer.emailAddress || null;
          customerLocal.mobileNumber = customer.mobileNumber || null;
          customerLocal.phoneNumber = customer.phoneNumber || null;
          customerLocal.createdAt = customer.createdAt;
          customerLocal.updatedAt = customer.updatedAt;
        });
      });
    });
  }

  const handleOnRemoveCustomer = async () => {
    return sale
      ?.removeCustomer()
      .then(() => navigation.goBack())
      .catch((e) => console.log(e));
  };

  const removeCustomerMarkup = sale?.customer.id ? (
    <View marginT-20>
      <Button
        label=" Remove Customer"
        isDestructive
        onPress={handleOnRemoveCustomer}
        disabled={!sale.canRemoveCustomer}
      ></Button>
    </View>
  ) : undefined;

  const showSaveButtons = isDirty;
  const disabled = !isDirty || !isValid;

  const footerMarkup = showSaveButtons ? (
    <Button
      label={"Save"}
      backgroundColor={Colors.action}
      color="white"
      disabled={disabled}
      onPress={methods.handleSubmit(onValidSubmit, console.log)}
    ></Button>
  ) : (
    removeCustomerMarkup
  );

  const customer = data?.customer;

  const overviewMarkup = customer ? (
    <Card title="Overview">
      <FlatActionList
        items={[
          {
            key: "spent",
            title: "Total Spent",
            componentRight: () => (
              <Text variant="heading1">
                {moneyToFormattedString(bankersRound(customer.amountSpend ?? 0))}
              </Text>
            ),
          },
          {
            key: "orders",
            title: "Orders",
            componentRight: () => (
              <Text variant="heading1">{customer.numberOfOrders ?? 0}</Text>
            ),
          },
          {
            key: "invoiced",
            title: "Invoiced Amount",
            componentRight: () => (
              <Text variant="heading1">
                {moneyToFormattedString(bankersRound(customer.invoicedAmount ?? 0))}
              </Text>
            ),
          },
          {
            key: "loyalty",
            title: "Loyalty Points",
            componentRight: () => (
              <Text variant="heading1">{customer.loyaltyPoints ?? 0} pts</Text>
            ),
          },
        ]}
      />
      {(customer.isTradeCustomer || customer.isRetailerCusAccount) && (
        <View
          style={{
            flexDirection: "row",
            padding: Spacings.s4,
            gap: Spacings.s2,
          }}
        >
          {customer.isTradeCustomer && <Badge>Trade</Badge>}
          {customer.isRetailerCusAccount && <Badge>Retailer Account</Badge>}
        </View>
      )}
    </Card>
  ) : null;

  const addressesMarkup =
    customer?.addresses && customer.addresses.length > 0 ? (
      <Card title="Addresses">
        <FlatActionList
          items={customer.addresses.map((addr) => ({
            key: addr.id,
            title: [addr.address1, addr.address2].filter(Boolean).join(", "),
            subTitle: `${addr.city} ${addr.state} ${addr.postcode}, ${addr.country}`,
            componentRight: addr.isPrimary
              ? () => <Badge>Primary</Badge>
              : undefined,
          }))}
        />
      </Card>
    ) : null;

  const salesHistoryMarkup = (
    <Card title="Sales History">
      {salesLoading && (
        <View style={{ padding: Spacings.s4 }}>
          <ActivityIndicator />
        </View>
      )}
      {!salesLoading && salesData?.sales.items.length === 0 && (
        <View style={{ padding: Spacings.s4, gap: Spacings.s2 }}>
          <Text variant="heading3">No purchase history yet</Text>
          {customer?.createdAt ? (
            <Text color="subdued">
              Customer since{" "}
              {formatDateToCalendar(customer.createdAt, fullDateTimeFormat)}
            </Text>
          ) : null}
        </View>
      )}
      {salesData?.sales.items.map((sale: Sale, idx: number) => (
        <Pressable
          key={sale.id}
          onPress={() => navigation.navigate("SaleScreen", { saleId: sale.id })}
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            paddingHorizontal: Spacings.s4,
            paddingVertical: Spacings.s3,
            borderBottomWidth: idx < salesData.sales.items.length - 1 ? 1 : 0,
            borderBottomColor: Colors.borderColor,
          }}
        >
          <View style={{ flex: 1 }}>
            <Text variant="heading" fontWeight="medium">
              #{sale.invoiceNumber}
            </Text>
            <Text variant="heading4" color="subdued">
              {formatDateToCalendar(sale.saleDate, fullDateTimeFormat)}
              {sale.location?.name ? ` · ${sale.location.name}` : ""}
            </Text>
          </View>
          <View style={{ flexDirection: "row", alignItems: "center", gap: Spacings.s2 }}>
            <View style={{ alignItems: "flex-end" }}>
              <Text variant="heading1">
                {moneyToFormattedString(bankersRound(sale.totalPrice))}
              </Text>
              <Badge status={sale.state as any}>{sale.state}</Badge>
            </View>
            <Text fontWeight="bold" variant="heading1" color="subdued">›</Text>
          </View>
        </Pressable>
      ))}
    </Card>
  );

  return (
    <Page
      title={
        customer
          ? `${customer.firstName} ${customer.lastName}`
          : "Edit Customer"
      }
      secondaryTitle="Customer Details"
      footer={footerMarkup}
    >
      <Stack vertical>
        {overviewMarkup}
        {addressesMarkup}
        <FormProvider {...methods}>
          <Form>
            <Card title="Customer Details">
              <CustomerDetailsForm />
            </Card>
          </Form>
        </FormProvider>
        {salesHistoryMarkup}
      </Stack>
    </Page>
  );
}
